import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useEffect, useRef, useState } from 'react';
import { Check, X } from '@/components/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { listPendingSerials, scanSerial, type PendingSerial, type ScanResult } from '@/api/whEntry';
import { formatDate } from '@/lib/format';
import { apiError } from '@/lib/apiError';

interface ScanLog {
  serialNo: string;
  ok: boolean;
  message: string;
  item?: ScanResult['item'];
  scannedAt?: string;
}

export function WhEntryScanPage() {
  const [input, setInput] = useState('');
  const [log, setLog] = useState<ScanLog[]>([]);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  // Auto-focus the scan input on mount and after each scan
  useEffect(() => { inputRef.current?.focus(); }, []);

  const pendingQuery = useQuery({
    queryKey: ['wh-entry-pending'],
    queryFn: () => listPendingSerials(),
    refetchInterval: 10_000,
  });
  const pending: PendingSerial[] = pendingQuery.data ?? [];

  async function handleScan() {
    const serial = input.trim();
    if (!serial) return;
    setInput('');
    setScanning(true);
    try {
      const result = await scanSerial(serial);
      setLog((prev) => [
        { serialNo: result.serialNo, ok: true, message: `${result.item.name}`, item: result.item, scannedAt: result.scannedAt },
        ...prev,
      ]);
      void qc.invalidateQueries({ queryKey: ['wh-entry-pending'] });
      void qc.invalidateQueries({ queryKey: ['wh-serials'] });
      void qc.invalidateQueries({ queryKey: ['wh-stock'] });
    } catch (err: unknown) {
      setLog((prev) => [{ serialNo: serial, ok: false, message: apiError(err, 'Scan failed') }, ...prev]);
    } finally {
      setScanning(false);
      inputRef.current?.focus();
    }
  }

  const scannedOk  = log.filter((l) => l.ok).length;
  const scannedErr = log.filter((l) => !l.ok).length;

  return (
    <div>
      <SectionHeader
        title="Warehouse Entry Scan"      />

      {/* KPI strip */}
      <div className="mb-2 grid grid-cols-3 gap-1.5 sm:mb-5 sm:gap-3">
        {[
          { label: 'Pending to Scan', value: pending.length, color: '#f59e0b' },
          { label: 'Scanned (session)', value: scannedOk,    color: '#22c55e' },
          { label: 'Errors (session)',  value: scannedErr,   color: '#ef4444' },
        ].map((k) => (
          <div
            key={k.label}
            className="flex min-w-0 flex-col rounded-lg p-2 text-white shadow-sm sm:min-w-[140px] sm:rounded-xl sm:px-4 sm:py-3"
            style={{ background: k.color, textShadow: KPI_TEXT_SHADOW }}
          >
            <span className="truncate text-[9px] font-bold uppercase tracking-wide text-white/90 sm:text-[11px]">{k.label}</span>
            <span className="text-lg font-extrabold sm:text-2xl">{k.value}</span>
          </div>
        ))}
      </div>

      {/* Scan input */}
      <div
        className="mb-2 rounded-lg border p-2 sm:mb-5 sm:rounded-xl sm:p-4"
        style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        <div className="mb-1 text-[10px] font-bold sm:mb-2 sm:text-[12px]" style={{ color: 'var(--text-muted)' }}>
          SCAN OR TYPE SERIAL NO — Press Enter to submit
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleScan(); }}
            disabled={scanning}
            placeholder="ITEMCODE-00001"
            className="min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm font-mono outline-none focus:ring-2"
            style={{
              background: 'var(--input-bg)',
              borderColor: 'var(--border-color)',
              color: 'var(--text-primary)',
            }}
            autoComplete="off"
            autoFocus
          />
          <button
            onClick={() => void handleScan()}
            disabled={scanning || !input.trim()}
            className="flex-shrink-0 rounded-lg px-3 py-2 text-[12px] font-bold text-white disabled:opacity-40 sm:px-5"
            style={{ background: '#22c55e' }}
          >
            {scanning ? 'Scanning…' : 'Confirm'}
          </button>
        </div>
      </div>

      {/* Two-column layout: scan log left, pending right */}
      <div className="grid gap-2 sm:gap-4 lg:grid-cols-2">

        {/* Scan log */}
        <div className="rounded-lg border sm:rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="border-b px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide sm:px-4 sm:py-2 sm:text-[11px]" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            Session Log
          </div>
          {log.length === 0 ? (
            <div className="px-3 py-4 text-center text-[11px] sm:px-4 sm:py-6 sm:text-[12px]" style={{ color: 'var(--text-muted)' }}>
              No scans yet — waiting for input
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto divide-y sm:max-h-[420px]" style={{ borderColor: 'var(--border-color)' }}>
              {log.map((entry, i) => (
                <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 sm:gap-3 sm:px-4 sm:py-2.5">
                  <span
                    className="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{
                      background: entry.ok ? '#22c55e22' : '#ef444422',
                      color: entry.ok ? '#22c55e' : '#ef4444',
                    }}
                  >
                    {entry.ok ? <Check size={14} /> : <X size={14} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[12px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                      {entry.serialNo}
                    </div>
                    <div className="text-[11px]" style={{ color: entry.ok ? 'var(--text-muted)' : '#ef4444' }}>
                      {entry.message}
                    </div>
                    {entry.scannedAt && (
                      <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(entry.scannedAt)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending serials */}
        <div className="rounded-lg border sm:rounded-xl" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="border-b px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide sm:px-4 sm:py-2 sm:text-[11px]" style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
            Pending Serials ({pending.length})
          </div>
          {pending.length === 0 ? (
            <div className="px-3 py-4 text-center text-[11px] sm:px-4 sm:py-6 sm:text-[12px]" style={{ color: 'var(--text-muted)' }}>
              {pendingQuery.isLoading ? 'Loading…' : 'All serials have been scanned!'}
            </div>
          ) : (
            <div className="max-h-[420px] overflow-y-auto divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {pending.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 px-2.5 py-1 sm:px-4 sm:py-2.5">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] font-bold truncate sm:text-[12px]" style={{ color: 'var(--text-primary)' }}>{s.serialNo}</div>
                    <div className="text-[10px] truncate sm:text-[11px]" style={{ color: 'var(--text-muted)' }}>{s.item.name}</div>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold sm:px-2 sm:text-[10px]"
                    style={{ background: '#f59e0b22', color: '#f59e0b' }}
                  >
                    Pending
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
